import React, { useCallback } from 'react';
import { RemoteDataUI } from '../../../common/ui/RemoteDataUI';
import { Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from "react-router-dom";
import { useFilterContext } from "../infrastructure/FilterContext";
import { FloatingDropdown } from "../../../ui/components/FloatingDropdown";
import { useHasCombinable } from "../../../cart/hooks/useHasCombinable";
import { useTenantContext } from "../../../tenant/TenantContext";
import styled from "styled-components";

/**
 * Constants for styling
 */
const FONT_SIZE_SMALL = '0.8em';
const MARGIN_LEFT = '0.9375rem';
const MARGIN_TOP = '0.3125rem';

/**
 * Styled component for dropdown titles
 */
const Title = styled.span`
    font-size: ${FONT_SIZE_SMALL};
    margin-left: ${MARGIN_LEFT};
    margin-top: ${MARGIN_TOP};
    font-weight: 400;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }

    @media (max-width: 1280px) {
        font-size: 0.7em;
    }
`;

const TitleColored = styled(Title)`
    color: ${({ $color }) => $color};
`;

export const SearchLinksFilter = () => {
    const { filterOptionsRemoteData, updateFilters } = useFilterContext();
    const navigate = useNavigate();
    const location = useLocation();
    const hasCombinable = useHasCombinable();
    const { primaryColor } = useTenantContext();

    const onClick = (taxonomyFilter) => {
        updateFilters({ taxonomyTree: taxonomyFilter });

        const shouldNavigateToCatalog = !location.pathname.startsWith('/articulos') || location.pathname !== '/articulos';
        if (shouldNavigateToCatalog) {
            navigate('/articulos');
        }
    };


    const onChangeMacrofamily = useCallback((macrofamilyId) => {
        onClick({
            macrofamilyId: macrofamilyId,
            familyId: null,
            subfamilyIds: new Set()
        });
    }, [onClick]);

    const onClickSpecialSection = useCallback((characteristics) => {
        updateFilters({ characteristics });
        navigate('/articulos');
    }, [updateFilters, navigate]);

    const onChangeFamily = useCallback((macrofamilyId, familyId) => {
        onClick({
            macrofamilyId: macrofamilyId,
            familyId: familyId,
            subfamilyIds: new Set()
        });
    }, [onClick]);

    const onChangeSubfamily = useCallback((macrofamilyId, familyId, subfamilyId) => {
        onClick({
            macrofamilyId: macrofamilyId,
            familyId: familyId,
            subfamilyIds: new Set([subfamilyId])
        });
    }, [onClick]);

    return (
        <RemoteDataUI remoteData={filterOptionsRemoteData}>
            <RemoteDataUI.Pending>
                <div className="d-flex justify-content-center align-items-center">
                    <Spinner size="sm" />
                </div>
            </RemoteDataUI.Pending>

            <RemoteDataUI.Success>
                {
                    filterOptions => (
                        <div className="row justify-content-around align-items-center">
                            {filterOptions.macrofamilias.map((macrofamily) => (
                                <div className="col-12 col-sm-6 col-md-auto" key={macrofamily.id}>
                                    <FloatingDropdown>
                                        <FloatingDropdown.Title>
                                            <Title
                                                className="fw-bold fs-6"
                                                onClick={() => onChangeMacrofamily(macrofamily.id)}
                                            >
                                                {macrofamily.macrofamilia}
                                            </Title>
                                        </FloatingDropdown.Title>
                                        <FloatingDropdown.Items>
                                            {macrofamily.familias.map((family) => (
                                                <FloatingDropdown key={family.id} placement="after">
                                                    <FloatingDropdown.Title>
                                                        <Title
                                                            onClick={() =>
                                                                onChangeFamily(macrofamily.id, family.id)
                                                            }
                                                        >
                                                            {family.familia}
                                                        </Title>
                                                    </FloatingDropdown.Title>
                                                    <FloatingDropdown.Items>
                                                        {family.subfamilias.map((subfamily) => (
                                                            <Title
                                                                key={subfamily.id}
                                                                onClick={() =>
                                                                    onChangeSubfamily(macrofamily.id, family.id, subfamily.id)
                                                                }
                                                            >
                                                                {subfamily.subfamilia}
                                                            </Title>
                                                        ))}
                                                    </FloatingDropdown.Items>
                                                </FloatingDropdown>
                                            ))}
                                        </FloatingDropdown.Items>
                                    </FloatingDropdown>
                                </div>
                            ))}
                            {hasCombinable && (
                                <div className="col-12 col-sm-6 col-md-auto">
                                    <TitleColored
                                        $color={primaryColor}
                                        className="fw-bold fs-6"
                                        onClick={() => onClickSpecialSection({ hasCombinable: true })}
                                    >
                                        Ofertas combinables
                                    </TitleColored>
                                </div>
                            )}
                            <div className="col-12 col-sm-6 col-md-auto">
                                <TitleColored
                                    $color={primaryColor}
                                    className="fw-bold fs-6"
                                    onClick={() => onClickSpecialSection({ hasActiveLotPromotion: true })}
                                >
                                    Ofertas corta caducidad
                                </TitleColored>
                            </div>
                        </div>

                    )
                }
            </RemoteDataUI.Success>
        </RemoteDataUI>
    );

};